import { useUserStore } from '@/store/modules/user'

export type PrintDocument = 'charge_receipt' | 'dispensing_receipt' | 'refund_receipt' | 'prescription' | 'treatment' | 'instruction' | 'medication_label' | 'patient_label'
export type PrintScope = 'charge' | 'pharmacy'
export type PrintResult = { status: 'submitted' | 'cancelled' | 'failed' | 'browser'; message?: string }
export type DesktopPrintOptions = { deviceName: string; preview: boolean; copies: number }
export type PrintPaperSize = { width: string; height?: string }
export type PrintOutputOptions = DesktopPrintOptions & { isActive?: () => boolean }
export type PrinterInfo = { name: string; displayName: string; isDefault: boolean; status: number }
export type PrintRule = DesktopPrintOptions & { id: PrintDocument; name: string; onNew: boolean | null; onPaid: boolean | null }

// 后端自动打印设置使用接口文档中的短横线编码，打印函数使用前端命令编码。
export const backendDocumentType: Record<PrintDocument, string> = {
  charge_receipt: 'receipt',
  dispensing_receipt: 'dispensing-receipt',
  refund_receipt: 'refund-receipt',
  prescription: 'prescription',
  treatment: 'treatment',
  instruction: 'medication-instruction',
  medication_label: 'medication-label',
  patient_label: 'patient-label',
}
export const frontendDocumentType = (value: string): PrintDocument | undefined =>
  (Object.keys(backendDocumentType) as PrintDocument[]).find(key => backendDocumentType[key] === value)

declare global {
  interface Window {
    zhongshengPrinter?: {
      version: number
      getPrinters: () => Promise<PrinterInfo[]>
      print: (payload: DesktopPrintOptions & { html: string; title: string; paperSize?: PrintPaperSize }) => Promise<PrintResult>
    }
  }
}

export const getPrinterBridge = () => window.zhongshengPrinter?.version === 1 ? window.zhongshengPrinter : undefined

// Column labels differ per page: charge prints a new/pending charge entry, pharmacy
// prints a new dispensing order. Only the trigger wording is page-specific.
export type RuleColumns = { onNew: string; onPaid: string | null }

// 药房没有「收费完成同时打印」，对应列不展示；退药小票同理无触发条件。
const pharmacyRules = (): PrintRule[] => [
  { id: 'dispensing_receipt', name: '发药小票', onNew: false, onPaid: null },
  { id: 'refund_receipt', name: '退药小票', onNew: false, onPaid: null },
  { id: 'prescription', name: '处方', onNew: false, onPaid: null, preview: true },
  { id: 'medication_label', name: '用药标签', onNew: false, onPaid: null },
  { id: 'patient_label', name: '患者标签', onNew: false, onPaid: null },
].map(row => ({ deviceName: '', preview: false, copies: 1, ...row })) as PrintRule[]

const chargeRules = (): PrintRule[] => [
  { id: 'charge_receipt', name: '收费小票', onNew: null, onPaid: false },
  { id: 'dispensing_receipt', name: '发药小票', onNew: null, onPaid: false },
  { id: 'refund_receipt', name: '退费小票', onNew: null, onPaid: null },
  { id: 'prescription', name: '处方', onNew: false, onPaid: false, preview: true },
  { id: 'treatment', name: '治疗理疗单', onNew: false, onPaid: false, preview: true },
  { id: 'instruction', name: '用药说明', onNew: false, onPaid: false },
  { id: 'medication_label', name: '用药标签', onNew: false, onPaid: false },
  { id: 'patient_label', name: '患者标签', onNew: false, onPaid: false },
].map(row => ({ deviceName: '', preview: false, copies: 1, ...row })) as PrintRule[]

export const ruleColumns: Record<PrintScope, RuleColumns> = {
  charge: { onNew: '有新的收费单时自动打印', onPaid: '收费完成同时打印' },
  pharmacy: { onNew: '有新发药单时自动打印', onPaid: null },
}

export const defaultPrintRules = (scope: PrintScope = 'charge'): PrintRule[] =>
  scope === 'pharmacy' ? pharmacyRules() : chargeRules()

// Device-local settings, isolated by the existing tenant/dept fields plus page scope:
// the pharmacy and charge pages keep independent rules on the same workstation.
export const getPrintScope = (scope: PrintScope = 'charge') => {
  const user = useUserStore()
  if (!user.hasLogged || user.getTenant == null || user.getDept == null) return null
  return JSON.stringify([user.getTenant, user.getDept, scope])
}
const storageKey = (scope: string) => `his:print-rules:v1:${scope}`

export function readPrintRules(scope = getPrintScope(), page: PrintScope = 'charge'): PrintRule[] {
  const defaults = defaultPrintRules(page)
  if (!scope) return defaults
  const raw = localStorage.getItem(storageKey(scope))
  if (!raw) return defaults
  const rows = JSON.parse(raw)
  if (!Array.isArray(rows)) throw new Error('本机打印规则损坏，请重新保存设置')
  return defaults.map(base => {
    const row = rows.find(item => item.id === base.id)
    if (!row) return base
    if (typeof row.deviceName !== 'string' || typeof row.preview !== 'boolean' || !Number.isInteger(row.copies) || row.copies < 1 || row.copies > 99 ||
      (base.onNew !== null && typeof row.onNew !== 'boolean') || (base.onPaid !== null && typeof row.onPaid !== 'boolean')) {
      throw new Error('本机打印规则无效，请重新保存设置')
    }
    return { ...base, deviceName: row.deviceName, preview: row.preview, copies: row.copies,
      onNew: base.onNew === null ? null : row.onNew, onPaid: base.onPaid === null ? null : row.onPaid }
  })
}

export function savePrintRules(rows: PrintRule[], scope: string, page: PrintScope = 'charge') {
  if (!scope || scope !== getPrintScope(page)) throw new Error('登录机构已变化，请重新打开打印设置')
  localStorage.setItem(storageKey(scope), JSON.stringify(rows))
}

export const nativeOptions = (rule?: PrintRule): DesktopPrintOptions | undefined =>
  getPrinterBridge() && rule?.deviceName
    ? { deviceName: rule.deviceName, copies: rule.copies, preview: rule.preview }
    : undefined
